import {
  AccountTree,
  AttachMoney,
  Business,
  CallMerge,
  Colorize,
  Delete,
  DoneAll,
  Email,
  Event,
  Folder,
  Gavel,
  LanguageOutlined,
  LocalGasStation,
  LocalShipping,
  Numbers,
  RvHookup,
  Payments,
  Photo,
  Receipt,
  Route,
  Scale,
  Schedule,
  Sell,
  Subject,
  Timer,
  Today,
  WarningAmber,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Switch,
  Tab,
  TextField,
  useTheme,
} from "@mui/material";
import { isEqual, mergeWith } from "lodash";
import { useEffect, useState } from "react";
import { SketchPicker } from "react-color";
import {
  Currency,
  GetOrganizationSettingsQuery,
  InvoicingProcess,
  Language,
  LoadType,
  MeasurementSystem,
  TrailerType,
  TransactionType,
} from "../../../graphql/generated";
import { usePrevious } from "../../../utils/hooks/usePrevious";
import DocumentsUploadModal from "../../common/DocumentsUploadModal";
import EnumSelect from "../../common/EnumSelect";
import TransactionTemplatesModal from "../../accounting/TransationTemplatesModal/TransactionTemplatesModal";
import { isQa } from "../../../config/endpoint";
import DemoUtils from "../DemoUtils/DemoUtils";
import languageLabel from "../../../utils/labels/languageLabel";
import trailerTypeLabel from "../../../utils/labels/trailerTypeLabel";
import { useTranslation } from "react-i18next";
import CustomFieldDefinitionsListContainer from "../../custom-field-definitions/CustomFieldsList";
import {
  currencyToSymbol,
  currencyToUnit,
} from "../../../providers/LocaleProvider";
import AddressInput from "../../asset-management/AddressInput";
import { Day, dayEnumSelectValue } from "../../../@types/day.enum";
import FeatureGuard, { Feature } from "../Access/FeatureGuard";
import NumberTextField from "../../common/NumberTextField";
import { testIds } from "./OrgSettingsEditor.testIds";

const liquidDefaultTrailerTypeOptions = {
  Tanker: TrailerType.Tanker,
  TankWagon: TrailerType.TankWagon,
};

type OrganizationSettingsEditorData =
  GetOrganizationSettingsQuery["organizationSettings"];

type OrgSettingsEditorProps = {
  organizationSettings: OrganizationSettingsEditorData | null;
  onSave: (data: OrganizationSettingsEditorData) => void;
  nextInvoiceNumber?: number | null;
  onSaveNextInvoiceNumber?: (nextInvoiceNumber: number) => void;
};

type DeepPartialOrganizationSettingsEditorData = {
  [key in keyof Exclude<
    OrganizationSettingsEditorData,
    null | undefined
  >]: Partial<Exclude<OrganizationSettingsEditorData, null | undefined>[key]>;
};
const OrgSettingsEditor = ({
  organizationSettings,
  onSave,
  nextInvoiceNumber,
  onSaveNextInvoiceNumber,
}: OrgSettingsEditorProps) => {
  const { t } = useTranslation("settings");
  const [activeTab, setActiveTab] = useState("GENERAL");
  const [localOrganizationSettings, setLocalOrganizationSettings] =
    useState<OrganizationSettingsEditorData | null>(organizationSettings);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [emailLogoModalOpen, setEmailLogoModalOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [chargeTemplatesModalOpen, setChargeTemplatesModalOpen] =
    useState(false);
  const [expenseTemplatesModalOpen, setExpenseTemplatesModalOpen] =
    useState(false);
  const [remitAddressModalOpen, setRemitAddressModalOpen] = useState(false);
  // Saved on its own, it is a counter and not part of the settings document
  const [localNextInvoiceNumber, setLocalNextInvoiceNumber] = useState(
    nextInvoiceNumber ? String(nextInvoiceNumber) : "",
  );
  useEffect(() => {
    setLocalNextInvoiceNumber(
      nextInvoiceNumber ? String(nextInvoiceNumber) : "",
    );
  }, [nextInvoiceNumber]);
  const parsedNextInvoiceNumber = Number.parseInt(localNextInvoiceNumber, 10);
  const singleNumberInvoicingOn =
    !!organizationSettings?.invoicing?.enableSingleNumberInvoicing;
  const canSaveNextInvoiceNumber =
    singleNumberInvoicingOn &&
    Number.isInteger(parsedNextInvoiceNumber) &&
    parsedNextInvoiceNumber >= 1 &&
    parsedNextInvoiceNumber !== nextInvoiceNumber;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setShowPicker(true);
  };

  const theme = useTheme();

  const previousOrganizationSettings = usePrevious(organizationSettings);
  useEffect(() => {
    if (!isEqual(previousOrganizationSettings, organizationSettings)) {
      setLocalOrganizationSettings(organizationSettings);
    }
  }, [organizationSettings, previousOrganizationSettings]);

  const updateOrganizationSettings = (
    updates: DeepPartialOrganizationSettingsEditorData,
    autoSave = false,
  ) => {
    const updatedSettings = mergeWith(
      {},
      localOrganizationSettings,
      updates,
      (objValue, srcValue) => {
        if (Array.isArray(objValue)) {
          return srcValue;
        }
      },
    );
    setLocalOrganizationSettings(updatedSettings);
    if (autoSave) {
      onSave(updatedSettings);
    }
  };
  return (
    <Box>
      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList
            onChange={(event, value) => setActiveTab(value)}
            aria-label="Organization settings tabs"
          >
            <Tab data-testid={testIds.orgSettingsEditorGeneral}
              label={t("general.label", "General")}
              value="GENERAL"
              aria-label="General"
            />
            <Tab data-testid={testIds.orgSettingsEditorDispatching}
              label={t("dispatching.label")}
              value="DISPATCHING"
              aria-label="Dispatching"
            />
            <Tab data-testid={testIds.orgSettingsEditorInvoicing}
              label={t("invoicing.label", "Invoicing")}
              value="INVOICING"
              aria-label="Invoicing"
            />
            <Tab data-testid={testIds.orgSettingsEditorRatecon}
              label={t("ratecon.label", "Ratecon")}
              value="RATECON"
              aria-label="Ratecon"
            />
            <Tab data-testid={testIds.orgSettingsEditorCustomField}
              label={t("customFields.label", "Custom Field")}
              value="CUSTOM_FIELDS"
              aria-label="Custom Fields"
            />
            {isQa ? (
              <Tab data-testid={testIds.orgSettingsEditorDemoUtils}
                label={t("demoUtils.label", "Demo Utils")}
                value="DEMO_UTILS"
                aria-label="Demo Utils"
              />
            ) : null}
          </TabList>
        </Box>
        <TabPanel value="GENERAL">
          <ListItem
            divider
            secondaryAction={
              <EnumSelect data-testid="org-settings-editor-driver-short-pay-method"
                label={t("general.language", "Language")}
                sx={{
                  width: 200,
                }}
                enumObject={Language}
                optionLabel={languageLabel}
                name="language"
                id="language-select"
                value={localOrganizationSettings?.general?.language || null}
                onChange={(event, value) => {
                  updateOrganizationSettings({
                    general: {
                      language: value,
                    },
                  });
                }}
              />
            }
          >
            <ListItemIcon>
              <LanguageOutlined />
            </ListItemIcon>
            <ListItemText
              primary={t("general.language", "Language")}
              secondary={t(
                "general.languageDesc",
                "Language used for the user interface. (if not set, the browser language will be used)",
              )}
            />
          </ListItem>
          <ListItem
            divider
            secondaryAction={
              <EnumSelect data-testid="org-settings-editor-driver-pay-frequency"
                label={t("general.measurementSystem", "Measurement System")}
                sx={{
                  width: 200,
                }}
                enumObject={MeasurementSystem}
                name="measurementSystem"
                id="measurement-system-select"
                value={
                  localOrganizationSettings?.general?.measurementSystem || null
                }
                onChange={(event, value) => {
                  updateOrganizationSettings({
                    general: {
                      measurementSystem: value,
                    },
                  });
                }}
              />
            }
          >
            <ListItemIcon>
              <Scale />
            </ListItemIcon>
            <ListItemText
              primary={t("general.measurementSystem", "Measurement System")}
              secondary={t(
                "general.measurementSystemDesc",
                "Determines the units used for displaying weights, distances, volumes, etc.",
              )}
            />
          </ListItem>

          <ListItem
            divider
            secondaryAction={
              <EnumSelect data-testid="org-settings-editor-driver-deduction-method"
                label={t("general.currency", "Currency")}
                sx={{
                  width: 200,
                }}
                enumObject={Currency}
                name="currency"
                id="currency-select"
                value={
                  localOrganizationSettings?.general?.currency || Currency.Usd
                }
                optionLabel={(currency) =>
                  `${currencyToUnit[currency]} - ${currencyToSymbol[currency]}`
                }
                onChange={(event, value) => {
                  updateOrganizationSettings({
                    general: {
                      currency: value,
                    },
                  });
                }}
              />
            }
          >
            <ListItemIcon>
              <AttachMoney />
            </ListItemIcon>
            <ListItemText
              primary={t("general.currency", "Currency")}
              secondary={t(
                "general.currencyDesc",
                "Determines the currency used for displaying monetary values.",
              )}
            />
          </ListItem>

          <ListItem
            divider
            secondaryAction={
              <EnumSelect data-testid="org-settings-editor-driver-deduction-frequency"
                label={t("general.weekStartsOn", "Week Start Day")}
                sx={{
                  width: 200,
                }}
                enumObject={Day}
                name="weekStartsOn"
                id="week-starts-on-select"
                value={dayEnumSelectValue(
                  localOrganizationSettings?.general?.weekStartsOn,
                )}
                onChange={(event, value) => {
                  updateOrganizationSettings({
                    general: {
                      weekStartsOn: Day[value as keyof typeof Day],
                    },
                  });
                }}
              />
            }
          >
            <ListItemIcon>
              <Today />
            </ListItemIcon>
            <ListItemText
              primary={t("general.weekStartsOn", "Week Start Day")}
              secondary={t(
                "general.weekStartsOnDesc",
                "Determines the day of the week that the calendar starts on.",
              )}
            />
          </ListItem>

          <ListItem
            divider
            secondaryAction={
              <Switch
                checked={
                  !!localOrganizationSettings?.general?.useSimpleWorkflow
                }
                onChange={(_, checked) => {
                  updateOrganizationSettings({
                    general: {
                      useSimpleWorkflow: checked,
                    },
                  });
                }}
              inputProps={{ "data-testid": testIds.orgSettingsEditorSimpleWorkflow } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
          >
            <ListItemIcon>
              <AccountTree />
            </ListItemIcon>
            <ListItemText
              primary={t("general.useSimpleFlow", "Use Simple Workflow")}
              secondary={t(
                "general.useSimpleFlowDesc",
                "When enabled, does not require driver to Load at a Stop. Still requires a driver to Arrive and Depart a location to track events",
              )}
            />
          </ListItem>

          <ListItem
            divider
            secondaryAction={
              <Switch
                data-testid={
                  testIds.orgSettingsEditorSingleClickCompletion
                }
                checked={
                  !!localOrganizationSettings?.general
                    ?.singleClickTripCompletion
                }
                onChange={(_, checked) => {
                  updateOrganizationSettings({
                    general: {
                      singleClickTripCompletion: checked,
                    },
                  });
                }}
                inputProps={{
                  "data-testid": `${testIds.orgSettingsEditorSingleClickCompletion}-input`,
                } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
          >
            <ListItemIcon>
              <DoneAll />
            </ListItemIcon>
            <ListItemText
              primary={t("general.singleClickTripCompletion")}
              secondary={t("general.singleClickTripCompletionDesc")}
            />
          </ListItem>

          <ListItem
            divider
            secondaryAction={
              <Switch
                data-testid={testIds.orgSettingsEditorFutureTrips}
                checked={
                  !!localOrganizationSettings?.general?.driversCanSeeFutureTrips
                }
                onChange={(_, checked) => {
                  updateOrganizationSettings({
                    general: {
                      driversCanSeeFutureTrips: checked,
                    },
                  });
                }}
                inputProps={{
                  "data-testid": `${testIds.orgSettingsEditorFutureTrips}-input`,
                } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
          >
            <ListItemIcon>
              <Event />
            </ListItemIcon>
            <ListItemText
              primary={t(
                "general.canSeeFutureTrips",
                "Drivers can see future trips",
              )}
              secondary={t(
                "general.canSeeFutureTripsDesc",
                "When enabled, drivers can future trips they are assigned to in their planning",
              )}
            />
          </ListItem>

          <ListItem
            divider
            secondaryAction={
              <Switch
                checked={
                  !!localOrganizationSettings?.dispatching?.canUseAdHocAddress
                }
                onChange={(_, checked) => {
                  updateOrganizationSettings({
                    dispatching: {
                      canUseAdHocAddress: checked,
                    },
                  });
                }}
              inputProps={{ "data-testid": testIds.orgSettingsEditorAdHocAddress } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
          >
            <ListItemIcon>
              <Event />
            </ListItemIcon>
            <ListItemText
              primary={t(
                "general.canUseAdHocAddress",
                "Enable Ad-Hoc addresses",
              )}
              secondary={t(
                "general.canUseAdHocAddressDesc",
                "When enabled, dispatchers can use ad-hoc addresses for locations",
              )}
            />
          </ListItem>

          <ListItem
            divider
            secondaryAction={
              <Switch
                checked={
                  !!localOrganizationSettings?.dispatching
                    ?.canDriverSetAlternateSupplierPinCode
                }
                onChange={(_, checked) => {
                  updateOrganizationSettings({
                    dispatching: {
                      canDriverSetAlternateSupplierPinCode: checked,
                    },
                  });
                }}
              inputProps={{ "data-testid": testIds.orgSettingsEditorAlternatePinCode } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
          >
            <ListItemIcon>
              <Numbers />
            </ListItemIcon>
            <ListItemText
              primary={t(
                "dispatching.canDriverSetAlternateSupplierPinCode",
                "Alternate Supplier PIN Codes for Driver",
              )}
              secondary={t(
                "dispatching.canDriverSetAlternateSupplierPinCodeDesc",
                "When a customer has multiple contracts for a commodity allow drivers to see these alternate contracts and change the contract Shipper/Supplier choice",
              )}
            />
          </ListItem>
        </TabPanel>

        <TabPanel value="DISPATCHING">
          <ListItem
            divider
            secondaryAction={
              <Switch
                checked={
                  !!localOrganizationSettings?.dispatching?.canChangeTripOrder
                }
                onChange={(_, checked) => {
                  updateOrganizationSettings({
                    dispatching: {
                      canChangeTripOrder: checked,
                    },
                  });
                }}
              inputProps={{ "data-testid": testIds.orgSettingsEditorChangeTripOrder } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
          >
            <ListItemIcon>
              <Route />
            </ListItemIcon>
            <ListItemText
              primary={t("dispatching.canChangeTripOrder")}
              secondary={t("dispatching.canChangeTripOrderDesc")}
            />
          </ListItem>
          <ListItem
            divider
            secondaryAction={
              <Switch
                checked={
                  !!localOrganizationSettings?.dispatching
                    ?.canConsolidateInProgressShipments
                }
                onChange={(_, checked) => {
                  updateOrganizationSettings({
                    dispatching: {
                      canConsolidateInProgressShipments: checked,
                    },
                  });
                }}
              inputProps={{ "data-testid": testIds.orgSettingsEditorConsolidateInProgressShipments } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
          >
            <ListItemIcon>
              <CallMerge />
            </ListItemIcon>
            <ListItemText
              primary={t("dispatching.canConsolidateInProgressShipments")}
              secondary={t("dispatching.canConsolidateInProgressShipmentsDesc")}
            />
          </ListItem>
          <FeatureGuard feature={Feature.CommodityManagement}>
            <ListItem
              divider
              secondaryAction={
                <Switch
                  checked={
                    !!localOrganizationSettings?.dispatching
                      ?.enableCapacityWarnings
                  }
                  onChange={(_, checked) => {
                    updateOrganizationSettings({
                      dispatching: {
                        enableCapacityWarnings: checked,
                      },
                    });
                  }}
                inputProps={{ "data-testid": testIds.orgSettingsEditorTankCapacityWarnings } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
            >
              <ListItemIcon>
                <WarningAmber />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "dispatching.enableCapacityWarnings",
                  "Enable Tank Capacity Warnings",
                )}
                secondary={t(
                  "dispatching.enableCapacityWarningsDesc",
                  "When enabled, shows warnings when delivery amount exceeds available tank capacity for monitored tanks during order entry",
                )}
              />
            </ListItem>
          </FeatureGuard>
          <FeatureGuard feature={Feature.CommodityManagement}>
            <ListItem
              divider
              secondaryAction={
                <Switch
                  checked={
                    !!localOrganizationSettings?.dispatching?.trackCommodity
                  }
                  onChange={(_, checked) => {
                    updateOrganizationSettings({
                      dispatching: {
                        trackCommodity: checked,
                      },
                    });
                  }}
                  inputProps={{ "data-testid": testIds.orgSettingsEditorTrackCommodity } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
            >
              <ListItemIcon>
                <RvHookup />
              </ListItemIcon>
              <ListItemText
                primary={t("dispatching.trackCommodity", "Track Commodity")}
                secondary={t(
                  "dispatching.trackCommodityDesc",
                  "When enabled, commodity left on trailer is tracked and used to assign order commodities",
                )}
              />
            </ListItem>
          </FeatureGuard>
          <ListItem
            divider
            secondaryAction={
              <Switch
                checked={
                  !!localOrganizationSettings?.dispatching
                    ?.trackNetAndGrossVolumes
                }
                onChange={(_, checked) => {
                  updateOrganizationSettings({
                    dispatching: {
                      trackNetAndGrossVolumes: checked,
                    },
                  });
                }}
                inputProps={
                  {
                    "data-testid":
                      testIds.orgSettingsEditorTrackNetAndGrossVolumes,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
          >
            <ListItemIcon>
              <LocalGasStation />
            </ListItemIcon>
            <ListItemText
              primary={t(
                "dispatching.netAndGrossGallons",
                "Net and Gross Gallons",
              )}
              secondary={t(
                "dispatching.netAndGrossGallonsDesc",
                "When enabled, drivers can enter Net gallons (in addition to Gross) on pickups and dropoffs, and dispatchers can see and edit both Net and Gross loaded and delivered amounts.",
              )}
            />
          </ListItem>
          <ListItem
            divider
            secondaryAction={
              <Switch
                checked={!!localOrganizationSettings?.dispatching?.enableEta}
                onChange={(_, checked) => {
                  updateOrganizationSettings({
                    dispatching: {
                      enableEta: checked,
                    },
                  });
                }}
                inputProps={
                  {
                    "data-testid": testIds.orgSettingsEditorEnableEta,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
          >
            <ListItemIcon>
              <Schedule />
            </ListItemIcon>
            <ListItemText
              primary={t("dispatching.enableEta", "Enable ETA")}
              secondary={t(
                "dispatching.enableEtaDesc",
                "When enabled, TrueTMS tracks the ETA of in progress orders and the next order of the driver, and shows ETA columns in the Planning Tool.",
              )}
            />
          </ListItem>
          <ListItem
            divider
            secondaryAction={
              <NumberTextField
                label={t("dispatching.dwellTimeLabel", "Dwell Time")}
                size="small"
                name="shipperDwellTimeInMinutes"
                value={
                  localOrganizationSettings?.dispatching
                    ?.shipperDwellTimeInMinutes ?? ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={
                  {
                    "data-testid": testIds.orgSettingsEditorShipperDwellTime,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
                placeholder="30 mns"
                onChange={(event) => {
                  updateOrganizationSettings({
                    dispatching: {
                      shipperDwellTimeInMinutes: event.target.value
                        ? Number(event.target.value)
                        : null,
                    },
                  });
                }}
              />
            }
          >
            <ListItemIcon>
              <Timer />
            </ListItemIcon>
            <ListItemText
              primary={t(
                "dispatching.shipperDwellTime",
                "Shipper Planned Dwell Time Default",
              )}
              secondary={t(
                "dispatching.shipperDwellTimeDescription",
                "This setting will set the default Planned Dwell time for all Shippers. This is the Planned Dwell Time that will be used if no Planned Dwell Time is set on the Shipper Profile. ( Default time is 30 minutes )",
              )}
              sx={{
                pr: 24,
              }}
            />
          </ListItem>
          <ListItem
            divider
            secondaryAction={
              <NumberTextField
                label={t("dispatching.dwellTimeLabel", "Dwell Time")}
                size="small"
                name="receiverDwellTimeInMinutes"
                value={
                  localOrganizationSettings?.dispatching
                    ?.receiverDwellTimeInMinutes ?? ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={
                  {
                    "data-testid": testIds.orgSettingsEditorReceiverDwellTime,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
                placeholder="30 mns"
                onChange={(event) => {
                  updateOrganizationSettings({
                    dispatching: {
                      receiverDwellTimeInMinutes: event.target.value
                        ? Number(event.target.value)
                        : null,
                    },
                  });
                }}
              />
            }
          >
            <ListItemIcon>
              <Timer />
            </ListItemIcon>
            <ListItemText
              primary={t(
                "dispatching.receiverDwellTime",
                "Receiver Planned Dwell Time Default",
              )}
              secondary={t(
                "dispatching.receiverDwellTimeDescription",
                "This setting will set the default Planned Dwell time for all Receivers. This is the Planned Dwell Time that will be used if no Planned Dwell Time is set on the Receiver Profile. ( Default time is 30 minutes )",
              )}
              sx={{
                pr: 24,
              }}
            />
          </ListItem>
          <ListItem
            divider
            secondaryAction={
              <EnumSelect data-testid="org-settings-editor-distance-unit"
                label={t("dispatching.orderTypeDefault", "Default Order Type")}
                sx={{ width: 220 }}
                enumObject={LoadType}
                optionLabel={(value) => {
                  const labels: Record<LoadType, string> = {
                    [LoadType.FullTruckLoad]: t(
                      "dispatching.fullTruckload",
                      "Full Truckload",
                    ),
                    [LoadType.LessThanTruckLoad]: t(
                      "dispatching.lessThanTruckload",
                      "Less than Truckload",
                    ),
                  };
                  return labels[value];
                }}
                name="orderTypeDefault"
                id="order-type-default-select"
                value={
                  localOrganizationSettings?.dispatching?.orderTypeDefault ??
                  LoadType.FullTruckLoad
                }
                onChange={(event, value) => {
                  updateOrganizationSettings({
                    dispatching: {
                      orderTypeDefault: value,
                    },
                  });
                }}
              />
            }
          >
            <ListItemIcon>
              <LocalShipping />
            </ListItemIcon>
            <ListItemText
              primary={t("dispatching.orderTypeDefault", "Default Order Type")}
              secondary={t(
                "dispatching.orderTypeDefaultDesc",
                "Sets the default Order Type pre-populated when creating a new Shipment. Full Truckload Shipments cannot be combined with other Shipments on the same Trip.",
              )}
              sx={{ pr: 24 }}
            />
          </ListItem>
          <FeatureGuard feature={Feature.CommodityManagement}>
            <ListItem
              divider
              secondaryAction={
                <EnumSelect
                  label={t(
                    "dispatching.defaultTrailerType",
                    "Default Trailer Type",
                  )}
                  sx={{ width: 220 }}
                  enumObject={liquidDefaultTrailerTypeOptions}
                  optionLabel={trailerTypeLabel}
                  name="defaultTrailerTypes"
                  id="default-trailer-types-select"
                  data-testid={
                    testIds.orgSettingsEditorDefaultTrailerType
                  }
                  multiple
                  value={
                    localOrganizationSettings?.dispatching?.defaultTrailerTypes
                      ?.length
                      ? localOrganizationSettings.dispatching
                          .defaultTrailerTypes
                      : [TrailerType.Tanker]
                  }
                  onChange={(event, value) => {
                    updateOrganizationSettings({
                      dispatching: {
                        defaultTrailerTypes: value,
                      },
                    });
                  }}
                />
              }
            >
              <ListItemIcon>
                <RvHookup />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "dispatching.defaultTrailerType",
                  "Default Trailer Type",
                )}
                secondary={t(
                  "dispatching.defaultTrailerTypeDesc",
                  "Use this setting to set the default Trailer Type on a new order.",
                )}
                sx={{ pr: 24 }}
              />
            </ListItem>
          </FeatureGuard>
        </TabPanel>
        <TabPanel value="INVOICING">
          <List>
            <ListItem
              divider
              secondaryAction={
                <Box>
                  <Button data-testid={testIds.orgSettingsEditorSelectLogo}
                    variant="outlined"
                    id="change-logo-button"
                    onClick={() => {
                      setLogoModalOpen(true);
                    }}
                  >
                    {!localOrganizationSettings?.invoicing?.logoUrl
                      ? t("select", "Select")
                      : t("change", "Change")}
                  </Button>
                  {localOrganizationSettings?.invoicing?.logoUrl ? (
                    <IconButton data-testid={testIds.orgSettingsEditorRemoveInvoiceLogo}
                      sx={{ ml: 1 }}
                      color="error"
                      id="remove-logo-button"
                      onClick={() => {
                        updateOrganizationSettings(
                          {
                            invoicing: {
                              logoUrl: null,
                            },
                          },
                          false,
                        );
                      }}
                    >
                      <Delete />
                    </IconButton>
                  ) : null}
                </Box>
              }
            >
              {localOrganizationSettings?.invoicing?.logoUrl ? (
                <ListItemAvatar>
                  <Avatar
                    alt={"Invoice logo"}
                    src={localOrganizationSettings?.invoicing?.logoUrl}
                  />
                </ListItemAvatar>
              ) : (
                <ListItemIcon>
                  <Photo />
                </ListItemIcon>
              )}
              <ListItemText
                primary={t("invoicing.logo", "Logo")}
                secondary={t(
                  "invoicing.logoDesc",
                  "Logo displayed at the top of your invoice. Should preferrably be 100x100px.",
                )}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <Box>
                  <Button data-testid={testIds.orgSettingsEditorSelectEmailLogo}
                    variant="outlined"
                    id="change-email-logo-button"
                    onClick={() => {
                      setEmailLogoModalOpen(true);
                    }}
                  >
                    {!localOrganizationSettings?.invoicing?.emailLogoUrl
                      ? t("select", "Select")
                      : t("change", "Change")}
                  </Button>
                  {localOrganizationSettings?.invoicing?.emailLogoUrl ? (
                    <IconButton data-testid={testIds.orgSettingsEditorRemoveEmailLogo}
                      sx={{ ml: 1 }}
                      color="error"
                      id="remove-email-logo-button"
                      onClick={() => {
                        updateOrganizationSettings(
                          {
                            invoicing: {
                              emailLogoUrl: null,
                            },
                          },
                          false,
                        );
                      }}
                    >
                      <Delete />
                    </IconButton>
                  ) : null}
                </Box>
              }
            >
              {localOrganizationSettings?.invoicing?.emailLogoUrl ? (
                <ListItemAvatar>
                  <Avatar
                    alt={"Customer email logo"}
                    src={localOrganizationSettings?.invoicing?.emailLogoUrl}
                  />
                </ListItemAvatar>
              ) : (
                <ListItemIcon>
                  <Photo />
                </ListItemIcon>
              )}
              <ListItemText
                primary={t("invoicing.emailLogo", "Customer Email Logo")}
                secondary={t(
                  "invoicing.emailLogoDesc",
                  "Logo displayed on the Tracking and Receiver document email. NOTE: Default is TrueTMS logo.",
                )}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <Box
                  sx={{
                    border: `3px solid ${theme.palette.primary.main}`,
                    padding: 0.3,
                    borderRadius: 2,
                  }}
                  display="flex"
                  alignItems="center"
                >
                  <Box data-testid={testIds.orgSettingsEditorInvoicePrimaryColor}
                    sx={{
                      cursor: "pointer",
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor:
                        localOrganizationSettings?.invoicing?.primaryColor ||
                        theme.palette.primary.main,
                    }}
                    onClick={handleClick}
                  />
                  <Popover
                    open={showPicker}
                    anchorEl={anchorEl}
                    onClose={() => setShowPicker(false)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                  >
                    <SketchPicker
                      disableAlpha
                      color={
                        localOrganizationSettings?.invoicing?.primaryColor ||
                        theme.palette.primary.main
                      }
                      onChange={(newColor) => {
                        updateOrganizationSettings({
                          invoicing: {
                            primaryColor: newColor.hex,
                          },
                        });
                      }}
                    />
                  </Popover>
                </Box>
              }
            >
              <ListItemIcon>
                <Colorize />
              </ListItemIcon>
              <ListItemText
                primary={t("invoicing.invoiceColor", "Invoice Color")}
                secondary={t(
                  "invoicing.invoiceColorDesc",
                  "Color used in the invoice tables. Click the box on the right to change it.",
                )}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <TextField
                  label={t("invoicing.prefix", "Prefix")}
                  size="small"
                  name="invoicePrefix"
                  value={localOrganizationSettings?.invoicing?.prefix || ""}
                  onChange={(event) => {
                    updateOrganizationSettings({
                      invoicing: {
                        prefix: event.target.value,
                      },
                    });
                  }}
                inputProps={{ "data-testid": testIds.orgSettingsEditorPrefix } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
            >
              <ListItemIcon>
                <Numbers />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "invoicing.invoiceNumberPrefix",
                  "Invoice Number Prefix",
                )}
                secondary={t(
                  "invoicing.invoiceNumberPrefixDesc",
                  "This will be added to the beginning of your invoice numbers.",
                )}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <Switch
                  checked={
                    !!localOrganizationSettings?.invoicing
                      ?.enableSingleNumberInvoicing
                  }
                  onChange={(event) => {
                    updateOrganizationSettings({
                      invoicing: {
                        enableSingleNumberInvoicing: event.target.checked,
                      },
                    });
                  }}
                  inputProps={{ "data-testid": testIds.orgSettingsEditorSingleNumberInvoicing } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
            >
              <ListItemIcon>
                <Numbers />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "invoicing.enableSingleNumberInvoicing",
                  "Enable Single Number Invoicing",
                )}
                secondary={t(
                  "invoicing.enableSingleNumberInvoicingDesc",
                  "When enabled, all invoices, single and consolidated, take their numbers from one sequence instead of the order number. Save, then check the next invoice number below.",
                )}
              />
            </ListItem>

            <ListItem divider>
              <ListItemIcon>
                <Numbers />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "invoicing.nextInvoiceNumber",
                  "Next Invoice Number",
                )}
                secondary={t(
                  "invoicing.nextInvoiceNumberDesc",
                  "The number the next invoice will get once single number invoicing is on. Set it above the highest invoice number in your previous accounting program.",
                )}
              />
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexShrink: 0, ml: 2 }}
              >
                <NumberTextField
                  label={t("invoicing.nextInvoiceNumber", "Next Invoice Number")}
                  size="small"
                  name="nextInvoiceNumber"
                  allowDecimal={false}
                  disabled={!singleNumberInvoicingOn}
                  value={localNextInvoiceNumber}
                  onChange={(event) => {
                    setLocalNextInvoiceNumber(event.target.value);
                  }}
                  sx={{ width: 180 }}
                  inputProps={{
                    min: 1,
                    "data-testid": testIds.orgSettingsEditorNextInvoiceNumber,
                  } as React.InputHTMLAttributes<HTMLInputElement>}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canSaveNextInvoiceNumber}
                  onClick={() => {
                    onSaveNextInvoiceNumber?.(parsedNextInvoiceNumber);
                  }}
                  data-testid={testIds.orgSettingsEditorSaveNextInvoiceNumber}
                >
                  {t("invoicing.updateNextInvoiceNumber", "Update")}
                </Button>
              </Stack>
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <Button data-testid={testIds.orgSettingsEditorConfigureChargeTypes}
                  variant="outlined"
                  onClick={() => {
                    setChargeTemplatesModalOpen(true);
                  }}
                >
                  {t("configure", "Configure")}
                </Button>
              }
            >
              <ListItemIcon>
                <Sell />
              </ListItemIcon>
              <ListItemText
                primary={t("invoicing.chargeTypes", "Charge Types")}
                secondary={t(
                  "invoicing.chargeTypesDesc",
                  "The line haul charge types that are available to use when invoicing a customer",
                )}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <Button data-testid={testIds.orgSettingsEditorConfigureAccessorialTypes}
                  variant="outlined"
                  onClick={() => {
                    setExpenseTemplatesModalOpen(true);
                  }}
                >
                  {t("configure", "Configure")}
                </Button>
              }
            >
              <ListItemIcon>
                <Payments />
              </ListItemIcon>
              <ListItemText
                primary={t("invoicing.accessorialTypes", "Accessorial Types")}
                secondary={t(
                  "invoicing.accessorialTypesDesc",
                  "The accessorial types that are available to use when invoicing a customer or adding an expense",
                )}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <EnumSelect data-testid="org-settings-editor-load-statuses"
                  label={t("invoicing.model", "Invoicing Model")}
                  sx={{
                    width: 200,
                  }}
                  enumObject={InvoicingProcess}
                  name="invoicingProcess"
                  id="invoicing-process-select"
                  multiple
                  value={
                    localOrganizationSettings?.invoicing?.processes || null
                  }
                  onChange={(event, value) => {
                    updateOrganizationSettings({
                      invoicing: {
                        processes: value,
                      },
                    });
                  }}
                />
              }
            >
              <ListItemIcon>
                <AccountTree />
              </ListItemIcon>
              <ListItemText
                primary={t("invoicing.model", "Invoicing Model")}
                secondary={t(
                  "invoicing.modelDesc",
                  "Determines whether your loads will be invoiced from the load details or from the billing queue.",
                )}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <Switch
                  checked={
                    !!localOrganizationSettings?.invoicing?.enableTaxRate
                  }
                  onChange={(_, checked) => {
                    updateOrganizationSettings({
                      invoicing: {
                        enableTaxRate: checked,
                      },
                    });
                  }}
                inputProps={{ "data-testid": testIds.orgSettingsEditorToggleTaxRate } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
            >
              <ListItemIcon>
                <Receipt />
              </ListItemIcon>
              <ListItemText
                primary={t("invoicing.enableTaxRate")}
                secondary={t("invoicing.enableTaxRateDesc")}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <Button data-testid={testIds.orgSettingsEditorConfigureRemitAddress}
                  variant="outlined"
                  onClick={() => {
                    setRemitAddressModalOpen(true);
                  }}
                >
                  {t("configure", "Configure")}
                </Button>
              }
            >
              <ListItemIcon>
                <Business />
              </ListItemIcon>
              <ListItemText
                primary={t("invoicing.remitAddress", "Remit Address")}
                secondary={t(
                  "invoicing.remitAddressDesc",
                  "The address that will be used on the invoice for remittance",
                )}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <Switch
                  checked={
                    !!localOrganizationSettings?.invoicing
                      ?.enableConsolidatedInvoicing
                  }
                  onChange={(_, checked) => {
                    updateOrganizationSettings({
                      invoicing: {
                        enableConsolidatedInvoicing: checked,
                      },
                    });
                  }}
                inputProps={{ "data-testid": testIds.orgSettingsEditorConsolidateShipments } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
            >
              <ListItemIcon>
                <Folder />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "invoicing.enableConsolidatedInvoicing",
                  "Enable Consolidated Invoicing",
                )}
                secondary={t(
                  "invoicing.enableConsolidatedInvoicingDesc",
                  "When enabled, invoices will be consolidatable either manually or automatically through a grouping criteria",
                )}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <Switch
                  checked={
                    !!localOrganizationSettings?.invoicing
                      ?.applyAllPrimaryRateTables
                  }
                  onChange={(_, checked) => {
                    updateOrganizationSettings({
                      invoicing: {
                        applyAllPrimaryRateTables: checked,
                      },
                    });
                  }}
                  inputProps={
                    {
                      'data-testid':
                        testIds.orgSettingsEditorApplyAllPrimaryRateTables,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
            >
              <ListItemIcon>
                <Receipt />
              </ListItemIcon>
              <ListItemText
                primary={t('invoicing.applyAllPrimaryRateTables')}
                secondary={t('invoicing.applyAllPrimaryRateTablesDesc')}
              />
            </ListItem>

            <ListItem
              divider
              secondaryAction={
                <Switch
                  checked={
                    !!localOrganizationSettings?.invoicing
                      ?.showLoadedDeliveredQuantities
                  }
                  onChange={(_, checked) => {
                    updateOrganizationSettings({
                      invoicing: {
                        showLoadedDeliveredQuantities: checked,
                      },
                    });
                  }}
                inputProps={{ "data-testid": testIds.orgSettingsEditorReceiverDocumentationSubjectTemplateToggle } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
            >
              <ListItemIcon>
                <Scale />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "invoicing.showLoadedDeliveredQuantities",
                  "Show Loaded/Delivered Quantities",
                )}
                secondary={t(
                  "invoicing.showLoadedDeliveredQuantitiesDesc",
                  "When enabled, the loaded and delivered quantities will be shown on the invoice",
                )}
              />
            </ListItem>

            <ListItem divider>
              <ListItemIcon>
                <Email />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "invoicing.receiverDocumentationSubjectTemplate",
                  "Receiver Documentation Subject Template",
                )}
                secondary={t(
                  "invoicing.receiverDocumentationSubjectTemplateDesc",
                  "This will be the subject of the email sent to the customer when you send them receiver documentation. Uses EJS templating.",
                )}
              />
            </ListItem>
            <Box>
              <TextField
                label={t(
                  "invoicing.receiverDocumentationSubjectTemplate",
                  "Receiver Documentation Subject Template",
                )}
                size="small"
                fullWidth
                name="receiverDocumentationSubjectTemplate"
                value={
                  localOrganizationSettings?.invoicing
                    ?.receiverDocumentationSubjectTemplate
                }
                onChange={(event) => {
                  updateOrganizationSettings({
                    invoicing: {
                      receiverDocumentationSubjectTemplate: event.target.value,
                    },
                  });
                }}
              inputProps={{ "data-testid": testIds.orgSettingsEditorReceiverDocumentationSubjectTemplateField } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Box>

            <ListItem>
              <ListItemIcon>
                <Subject />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "invoicing.receiverDocumentationBodyTemplate",
                  "Receiver Documentation Body Template",
                )}
                secondary={t(
                  "invoicing.receiverDocumentationBodyTemplateDesc",
                  "This will be the body of the email sent to the customer when you send them receiver documentation. Uses EJS templating.",
                )}
              />
            </ListItem>
            <Box>
              <TextField
                label={t(
                  "invoicing.receiverDocumentationBodyTemplate",
                  "Receiver Documentation Body Template",
                )}
                size="small"
                multiline
                minRows={5}
                maxRows={7}
                fullWidth
                name="receiverDocumentationBodyTemplate"
                value={
                  localOrganizationSettings?.invoicing
                    ?.receiverDocumentationBodyTemplate || ""
                }
                onChange={(event) => {
                  updateOrganizationSettings({
                    invoicing: {
                      receiverDocumentationBodyTemplate: event.target.value,
                    },
                  });
                }}
              inputProps={{ "data-testid": testIds.orgSettingsEditorReceiverDocumentationBodyTemplate } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Box>
          </List>
        </TabPanel>

        <TabPanel value="RATECON">
          <List>
            <ListItem>
              <ListItemIcon>
                <Gavel />
              </ListItemIcon>
              <ListItemText
                primary={t("ratecon.tac", "Ratecon Terms and Conditions")}
                secondary={t(
                  "ratecon.tacDesc",
                  "This will be added to 2nd page of the RATECONs you generate.",
                )}
              />
            </ListItem>

            <Box>
              <TextField
                label={t("ratecon.conditions", "Conditions")}
                size="small"
                multiline
                minRows={5}
                maxRows={7}
                fullWidth
                name="rateconTermsAndConditions"
                value={
                  localOrganizationSettings?.ratecon?.termsAndConditions || ""
                }
                onChange={(event) => {
                  updateOrganizationSettings({
                    ratecon: {
                      termsAndConditions: event.target.value,
                    },
                  });
                }}
              inputProps={{ "data-testid": testIds.orgSettingsEditorRateconTermsAndConditions } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Box>

            <ListItem divider>
              <ListItemIcon>
                <Email />
              </ListItemIcon>
              <ListItemText
                primary={t("ratecon.subjectTemplate", "Email Subject Template")}
                secondary={t(
                  "ratecon.subjectTemplateDesc",
                  "This will be the subject of the email sent to the customer when you send them a RATECON. Uses EJS templating.",
                )}
              />
            </ListItem>
            <Box>
              <TextField
                label={t("ratecon.subjectTemplate", "Email Subject Template")}
                size="small"
                fullWidth
                name="rateconSubjectTemplate"
                value={
                  localOrganizationSettings?.ratecon?.subjectTemplate || ""
                }
                onChange={(event) => {
                  updateOrganizationSettings({
                    ratecon: {
                      subjectTemplate: event.target.value,
                    },
                  });
                }}
              inputProps={{ "data-testid": testIds.orgSettingsEditorEmailSubjectTemplate } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Box>

            <ListItem>
              <ListItemIcon>
                <Subject />
              </ListItemIcon>
              <ListItemText
                primary={t("ratecon.bodyTemplate", "Email Body Template")}
                secondary={t(
                  "ratecon.bodyTemplateDesc",
                  "This will be the body of the email sent to the customer when you send them a RATECON. Uses EJS templating.",
                )}
              />
            </ListItem>
            <Box>
              <TextField
                label={t("ratecon.bodyTemplate", "Email Body Template")}
                size="small"
                multiline
                minRows={5}
                maxRows={7}
                fullWidth
                name="rateconBodyTemplate"
                value={localOrganizationSettings?.ratecon?.bodyTemplate || ""}
                onChange={(event) => {
                  updateOrganizationSettings({
                    ratecon: {
                      bodyTemplate: event.target.value,
                    },
                  });
                }}
              inputProps={{ "data-testid": testIds.orgSettingsEditorEmailBodyTemplate } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Box>
          </List>
        </TabPanel>

        <TabPanel value="DEMO_UTILS">
          <DemoUtils />
        </TabPanel>

        <TabPanel value="CUSTOM_FIELDS">
          <CustomFieldDefinitionsListContainer />
        </TabPanel>
      </TabContext>
      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{
          pr: 3,
        }}
      >
        <Button data-testid={testIds.orgSettingsEditorSaveSettings}
          variant="contained"
          color="primary"
          disabled={isEqual(organizationSettings, localOrganizationSettings)}
          onClick={() => {
            if (!localOrganizationSettings) {
              return null;
            }
            onSave(localOrganizationSettings);
          }}
          id="save-settings-button"
        >
          {t("save", "Save")}
        </Button>
      </Stack>
      <DocumentsUploadModal
        isOpen={logoModalOpen}
        multiple={false}
        hasLabel={false}
        title={t("general.uploadYourLogo", "Upload your logo")}
        accept="image/*"
        onCancel={() => {
          setLogoModalOpen(false);
        }}
        onSubmit={(documents) => {
          updateOrganizationSettings(
            {
              invoicing: {
                logoUrl: documents[0].url,
              },
            },
            true,
          );
          setLogoModalOpen(false);
        }}
      />
      <DocumentsUploadModal
        isOpen={emailLogoModalOpen}
        multiple={false}
        hasLabel={false}
        title={t("general.uploadYourEmailLogo", "Upload your email logo")}
        accept="image/*"
        onCancel={() => {
          setEmailLogoModalOpen(false);
        }}
        onSubmit={(documents) => {
          updateOrganizationSettings(
            {
              invoicing: {
                emailLogoUrl: documents[0].url,
              },
            },
            true,
          );
          setEmailLogoModalOpen(false);
        }}
      />
      <TransactionTemplatesModal
        open={chargeTemplatesModalOpen}
        templates={
          organizationSettings?.transactionTemplates?.filter(
            (t) => t.type === TransactionType.Income,
          ) || []
        }
        templateType={TransactionType.Income}
        onClose={() => {
          setChargeTemplatesModalOpen(false);
        }}
        onSubmit={(data) => {
          updateOrganizationSettings(
            {
              transactionTemplates: [
                ...data,
                ...(organizationSettings?.transactionTemplates?.filter(
                  (t) => t.type !== TransactionType.Income,
                ) || []),
              ],
            },
            true,
          );
          setChargeTemplatesModalOpen(false);
        }}
      />
      <TransactionTemplatesModal
        open={expenseTemplatesModalOpen}
        templates={
          organizationSettings?.transactionTemplates?.filter(
            (t) => t.type === TransactionType.Expense,
          ) || []
        }
        templateType={TransactionType.Expense}
        onClose={() => {
          setExpenseTemplatesModalOpen(false);
        }}
        onSubmit={(data) => {
          updateOrganizationSettings(
            {
              transactionTemplates: [
                ...data,
                ...(organizationSettings?.transactionTemplates?.filter(
                  (t) => t.type !== TransactionType.Expense,
                ) || []),
              ],
            },
            true,
          );
          setExpenseTemplatesModalOpen(false);
        }}
      />
      <Dialog
        open={remitAddressModalOpen}
        onClose={() => setRemitAddressModalOpen(false)}
      >
        <DialogTitle>
          {t("invoicing.remitAddress", "Remit Address")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }} />
          <AddressInput
            name="remitAddress"
            value={localOrganizationSettings?.invoicing?.remitAddress || null}
            onChange={(address) => {
              if (!address) {
                updateOrganizationSettings({
                  invoicing: {
                    remitAddress: null,
                  },
                });
                return;
              }

              if (
                !address.line1 &&
                !address.line2 &&
                !address.city &&
                !address.state &&
                !address.postalCode &&
                !address.country
              ) {
                updateOrganizationSettings({
                  invoicing: {
                    remitAddress: null,
                  },
                });
                return;
              }

              updateOrganizationSettings({
                invoicing: {
                  remitAddress: address,
                },
              });
            }}
            disableAutocomplete
          />
        </DialogContent>
        <DialogActions>
          <Button data-testid={testIds.orgSettingsEditorCancel}
            onClick={() => setRemitAddressModalOpen(false)}
            color="primary"
          >
            {t("cancel", "Cancel")}
          </Button>
          <Button data-testid={testIds.orgSettingsEditorSave}
            onClick={() => {
              setRemitAddressModalOpen(false);
              onSave(localOrganizationSettings);
            }}
            color="primary"
            variant="contained"
          >
            {t("save", "Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrgSettingsEditor;
